import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import {DecimalPipe, registerLocaleData} from '@angular/common';
import {TranslateLoader, provideTranslateService} from '@ngx-translate/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MAT_DATE_LOCALE} from '@angular/material/core';
import {BrowserModule} from '@angular/platform-browser';
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import {Router} from '@angular/router';
import localeDE from '@angular/common/locales/de';
import {StepService} from '../services/step.service';
import {AdvisorSharedModule} from '../advisor.shared.module';
import {PortalService} from '../services/portal.service';
import {StancheckComponent} from "./stancheck.component";
import {StancheckRoutingModule} from "./stancheck-routing.module";
import {GebaeudeStartComponent} from "./gebaeude/gebaeude.start.component";
import {GebaeudeStepComponent} from './gebaeude/gebaeude.step.component';
import {ErgebnisComponent} from "./ergebnis/ergebnis.component";
import {HaushaltStepComponent} from "./haushalt/haushalt.step.component";
import {HaushaltStartComponent} from "./haushalt/haushalt.start.component";
import {GebaeudeErgebnisComponent} from "./gebaeude/gebaeude.ergebnis.component";
import {EingabeStepComponent} from "./eingabe/eingabe.step.component";
import {HaushaltStepGeraetComponent} from "./haushalt/haushalt.step.geraet.component";
import {AppTranslatePipe} from "../pipe/AppTranslatePipe";
import {HaushaltErgebnisComponent} from "./haushalt/haushalt.ergebnis.component";
import {BaseChartDirective, provideCharts, withDefaultRegisterables} from "ng2-charts";
import {MatExpansionModule} from "@angular/material/expansion";

// global definition of language prefix
declare let advisorTranslationPrefix;

// AoT requires an exported function for factories
export function HttpLoaderFactory() {
  const http = inject(HttpClient);
  return {
    getTranslation: (lang: string) => http.get<object>(
      (typeof advisorTranslationPrefix !== 'undefined' ? advisorTranslationPrefix : 'translations/' + PortalService.defaultPortal + '/stancheck/') + lang + '.json'
    )
  };
}

// load the locales dynamic, based on the users browser language with fallback
const browserLocale = getBrowsersLocale();
switch (browserLocale.substring(0, 2)) {
  case 'de':
    registerLocaleData(localeDE, 'de');
    break;
}

/**
 * returns the users locale from the browser
 */
function getBrowsersLocale() {
  /* tslint:disable:no-string-literal */
  if (navigator['browserLanguage']) {
    return navigator['browserLanguage'];
  }
  else if (navigator.language) {
    return navigator.language;
  }
  /* tslint:enable:no-string-literal */
}

@NgModule({
  declarations: [
    StancheckComponent,
    EingabeStepComponent,
    GebaeudeStartComponent,
    GebaeudeStepComponent,
    GebaeudeErgebnisComponent,
    HaushaltStartComponent,
    HaushaltStepComponent,
    HaushaltStepGeraetComponent,
    HaushaltErgebnisComponent,
    ErgebnisComponent
  ],
  bootstrap: [
    StancheckComponent
  ],
  imports: [
    BrowserModule,
    StancheckRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    AdvisorSharedModule,
    BaseChartDirective,
    MatExpansionModule,
  ],
  providers: [
    {provide: MAT_DATE_LOCALE, useValue: 'de-DE'},
    {provide: LOCALE_ID, useValue: getBrowsersLocale()},
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        dialogOptions: {
          user: {
            name: 'dummy',
            email: 'dummy@co2online.de'
          }
        }
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const initializerFn = () => {
        inject(Sentry.TraceService)
      };
        return initializerFn();
      }),
    StepService,
    provideTranslateService({loader: {provide: TranslateLoader, useFactory: HttpLoaderFactory}}),
    provideHttpClient(withInterceptorsFromDi()),
    AppTranslatePipe,
    DecimalPipe,
    provideCharts(withDefaultRegisterables())
  ]
})
export class StancheckModule {
}
